'use client';

import { produce } from 'immer';
import { FC, ReactNode } from 'react';

import type { ModalName } from './modalStore.type';
import { storeFactory } from './storeFactory';

interface ModalStoreStateData {
  activeModal: ModalName | null;
}

interface ModalStoreStateFunctions {
  openModal: (modalName: ModalName) => void;
  closeModal: (modalName: ModalName) => void;
  closeAllModals: () => void;
  isModalOpen: (modalName: ModalName) => boolean;
}

const {
  store: modalStore,
  StoreProvider: ModalStoreProvider,
  useCreateStore: useCreateModalStore,
} = storeFactory<ModalStoreStateData, ModalStoreStateFunctions>(
  () => ({
    activeModal: null,
  }),
  (set, get) => ({
    openModal: (modalName) => {
      set((state) =>
        produce(state, (draft) => {
          draft.activeModal = modalName;
        }),
      );
    },
    closeModal: (modalName) => {
      if (modalName === get().activeModal) {
        set((state) =>
          produce(state, (draft) => {
            draft.activeModal = null;
          }),
        );
      }
    },
    closeAllModals: () => {
      set((state) =>
        produce(state, (draft) => {
          draft.activeModal = null;
        }),
      );
    },
    isModalOpen: (modalName) => {
      return get().activeModal === modalName;
    },
  }),
);

export { modalStore };

export const ModalStore: FC<{ children: ReactNode }> = ({ children }) => {
  const createModalStore = useCreateModalStore({});

  return <ModalStoreProvider createStore={createModalStore}>{children}</ModalStoreProvider>;
};
